import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { KineticLine, FadeUp } from "@/components/KineticText";
import { ALBUM_PHOTOS } from "@/data/site";

const PHOTOS = ALBUM_PHOTOS;

export default function Album() {
  const [selected, setSelected] = useState(null);

  const close = () => setSelected(null);
  const step = (dir) =>
    setSelected((s) => (s === null ? s : (s + dir + PHOTOS.length) % PHOTOS.length));

  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <section data-testid="album-section" className="mx-auto max-w-[1400px] px-6 pt-36 pb-24 md:px-12 md:pt-48 md:pb-40">
        <FadeUp>
          <p className="text-xs font-medium uppercase tracking-[0.35em] text-dusk">Álbum</p>
        </FadeUp>

        <h1
          data-testid="album-title"
          className="mt-6 font-serif font-light leading-[0.98] text-navy"
          style={{ fontSize: "clamp(2.75rem, 7vw, 6rem)" }}
        >
          <KineticLine>Momentos</KineticLine>
          <KineticLine delay={0.12}>
            <em className="italic text-sea">no mar.</em>
          </KineticLine>
        </h1>

        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-dusk md:text-lg">
            Uma seleção de horizontes, decks e pores do sol. Clique em uma foto para
            ver de perto — e em breve, as suas também estarão aqui.
          </p>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-24 md:grid-cols-12 md:gap-6">
          {PHOTOS.map((photo, i) => (
            <motion.figure
              key={photo.src}
              data-testid={`album-photo-${i}`}
              className={`${photo.span} sm:col-span-1`}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: (i % 2) * 0.08 }}
            >
              <button
                onClick={() => setSelected(i)}
                data-testid={`album-photo-button-${i}`}
                aria-label={`Abrir foto: ${photo.caption}`}
                className="group relative block w-full overflow-hidden bg-mist"
              >
                <img
                  src={photo.src}
                  alt={photo.caption}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.045] ${photo.aspect}`}
                />
                <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 bg-gradient-to-t from-navy/60 to-transparent px-5 pb-4 pt-16 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <span className="text-left font-serif text-base italic text-paper md:text-lg">{photo.caption}</span>
                  <span className="font-serif text-xs italic text-paper/70">
                    {String(i + 1).padStart(2, "0")} / {String(PHOTOS.length).padStart(2, "0")}
                  </span>
                </figcaption>
              </button>
            </motion.figure>
          ))}
        </div>

        <FadeUp delay={0.1} className="mt-16 md:mt-24">
          <p className="border-t border-cloud pt-8 text-center font-serif text-lg italic text-dusk md:text-xl">
            Em breve: as fotos das suas viagens com a SG Turismos.
          </p>
        </FadeUp>
      </section>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            data-testid="lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/95 p-4 backdrop-blur-md md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              aria-label="Fechar foto"
              onClick={close}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-navy"
            >
              <X size={18} />
            </button>

            <button
              data-testid="lightbox-prev"
              aria-label="Foto anterior"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-navy md:left-8"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="lightbox-next"
              aria-label="Próxima foto"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-navy md:right-8"
            >
              <ArrowRight size={18} />
            </button>

            <motion.figure
              key={selected}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="flex max-h-full flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={PHOTOS[selected].src}
                alt={PHOTOS[selected].caption}
                data-testid="lightbox-image"
                className="max-h-[78vh] w-auto max-w-full object-contain"
              />
              <figcaption className="mt-5 flex items-baseline gap-4 font-serif text-lg italic text-paper/90">
                <span>{PHOTOS[selected].caption}</span>
                <span className="text-xs not-italic text-paper/50">
                  {String(selected + 1).padStart(2, "0")} / {String(PHOTOS.length).padStart(2, "0")}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.main>
  );
}
